import React, { useState, useEffect } from 'react';

interface AppVersion {
  id: number;
  component: string;
  version: string;
  build_number: string | null;
  git_commit: string | null;
  release_date: string | null;
  changelog: string | null;
  is_current: boolean;
  created_at: string;
}

interface StoreVersion {
  component: string;
  store_version: string | null;
  store_build: string | null;
  last_checked: string | null;
  error?: string;
}

interface VersionManagementProps {
  apiUrl: (path: string) => string;
  getAuthHeaders: (payload?: any, method?: string) => Record<string, string>;
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const VersionManagement: React.FC<VersionManagementProps> = ({ apiUrl, getAuthHeaders, safeFetch = fetch }) => {
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [storeVersions, setStoreVersions] = useState<StoreVersion[]>([]);
  const [backendVersion, setBackendVersion] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    component: '',
    version: '',
    build_number: '',
    git_commit: '',
    changelog: '',
    is_current: true
  });
  const [error, setError] = useState<string>('');

  const components = ['backend', 'ios-zivodoc', 'ios-zivohealth', 'android-zivodoc', 'android-zivohealth'];

  const fetchVersions = async (component?: string) => {
    setLoading(true);
    setError('');
    try {
      const url = component 
        ? `/api/v1/admin/versions?component=${component}`
        : '/api/v1/admin/versions';
      const response = await safeFetch(apiUrl(url), { headers: getAuthHeaders() });
      
      if (response.status === 401 || response.status === 403) {
        setError('Unauthorized. Please refresh and login again.');
        return;
      }
      
      if (!response.ok) {
        const text = await response.text();
        setError(`Failed to fetch versions: ${response.status} ${text}`);
        return;
      }
      
      const data = await response.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(`Error fetching versions: ${e.message}`);
      console.error('Error fetching versions:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackendVersion = async () => {
    try {
      const response = await safeFetch(apiUrl('/api/v1/version'), { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setBackendVersion(data);
      }
    } catch (e) {
      console.error('Error fetching backend version:', e);
    }
  };

  const fetchStoreVersions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await safeFetch(apiUrl('/api/v1/admin/versions/store-versions'), { 
        headers: getAuthHeaders() 
      });
      
      if (response.status === 401 || response.status === 403) {
        setError('Unauthorized. Please refresh and login again.');
        return;
      }
      
      if (!response.ok) {
        const text = await response.text();
        setError(`Failed to fetch store versions: ${response.status} ${text}`);
        return;
      }
      
      const data = await response.json();
      setStoreVersions(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(`Error fetching store versions: ${e.message}`);
      console.error('Error fetching store versions:', e);
    } finally {
      setLoading(false);
    }
  };

  const createVersion = async () => {
    if (!formData.component || !formData.version) {
      setError('Component and version are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        component: formData.component,
        version: formData.version,
        build_number: formData.build_number || null,
        git_commit: formData.git_commit || null,
        changelog: formData.changelog || null,
        is_current: formData.is_current
      };

      const response = await safeFetch(apiUrl('/api/v1/admin/versions'), {
        method: 'POST',
        headers: getAuthHeaders(payload, 'POST'),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
        setError(`Failed to create version: ${response.status} ${text}`);
        return;
      }

      const newVersion = await response.json();
      setVersions(prev => [newVersion, ...prev]);
      setShowCreateForm(false);
      setFormData({
        component: '',
        version: '',
        build_number: '',
        git_commit: '',
        changelog: '',
        is_current: true
      });
      alert('Version created successfully!');
    } catch (e: any) {
      setError(`Error creating version: ${e.message}`);
      console.error('Error creating version:', e);
    } finally {
      setLoading(false);
    }
  };

  const updateVersionCurrent = async (versionId: number, isCurrent: boolean) => {
    setLoading(true);
    setError('');
    try {
      const payload = { is_current: isCurrent };
      const response = await safeFetch(apiUrl(`/api/v1/admin/versions/${versionId}`), {
        method: 'PUT',
        headers: getAuthHeaders(payload, 'PUT'),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
        setError(`Failed to update version: ${response.status} ${text}`);
        return;
      }

      await fetchVersions(selectedComponent || undefined);
      alert('Version updated successfully!');
    } catch (e: any) {
      setError(`Error updating version: ${e.message}`);
      console.error('Error updating version:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
    fetchBackendVersion();
    fetchStoreVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedComponent) {
      fetchVersions(selectedComponent);
    } else {
      fetchVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComponent]);

  const filteredVersions = selectedComponent 
    ? versions.filter(v => v.component === selectedComponent)
    : versions;

  const currentVersions = versions.filter(v => v.is_current);

  return (
    <div className="monitoring-section">
      <div className="section-header">
        <div className="section-title">Version Management</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button 
            className={`refresh-button ${loading ? 'refreshing' : ''}`} 
            onClick={() => {
              fetchVersions(selectedComponent || undefined);
              fetchBackendVersion();
              fetchStoreVersions();
            }}
            disabled={loading}
            title="Refresh Versions"
          >
            {loading ? '⏳' : '🔄'}
          </button>
          <button 
            className="view-workflow-btn" 
            style={{ background: '#28a745' }}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ New Version'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ 
          padding: '12px', 
          background: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '4px', 
          marginBottom: '16px' 
        }}>
          {error}
        </div>
      )}

      {/* Current Backend Version */}
      {backendVersion && (
        <div style={{ 
          padding: '16px', 
          background: '#e7f3ff', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #b3d9ff'
        }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#004085' }}>Current Backend Version</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <strong>Version:</strong> {backendVersion.backend_version}
            </div>
            <div>
              <strong>API Version:</strong> {backendVersion.api_version}
            </div>
            <div>
              <strong>Build Date:</strong> {backendVersion.build_date || 'N/A'}
            </div>
            <div>
              <strong>Git Commit:</strong> {backendVersion.git_commit || 'N/A'}
            </div>
            <div>
              <strong>Environment:</strong> {backendVersion.environment || 'N/A'}
            </div>
          </div>
        </div>
      )}

      {/* Store Versions */}
      {storeVersions.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Latest Store Versions</h3>
          <div className="endpoints-table">
            <div className="table-header">
              <div>Component</div>
              <div>Store Version</div>
              <div>Store Build</div>
              <div>Last Checked</div>
              <div>Status</div>
            </div>
            {storeVersions.map((sv, idx) => (
              <div key={idx} className="table-row">
                <div>{sv.component}</div>
                <div>{sv.store_version || '-'}</div>
                <div>{sv.store_build || '-'}</div>
                <div>{sv.last_checked ? new Date(sv.last_checked).toLocaleString() : 'Never'}</div>
                <div>
                  {sv.error ? (
                    <span style={{ color: '#dc3545' }}>Error: {sv.error}</span>
                  ) : (
                    <span style={{ color: '#28a745' }}>✓ Available</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Version Form */}
      {showCreateForm && (
        <div style={{ 
          padding: '20px', 
          background: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ marginTop: 0 }}>Create New Version</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Component *</label>
              <select
                value={formData.component}
                onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Select component</option>
                {components.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Version * (e.g., 1.0.0)</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0.0"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Build Number</label>
              <input
                type="text"
                value={formData.build_number}
                onChange={(e) => setFormData({ ...formData, build_number: e.target.value })}
                placeholder="20240120.001"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Git Commit</label>
              <input
                type="text"
                value={formData.git_commit}
                onChange={(e) => setFormData({ ...formData, git_commit: e.target.value })}
                placeholder="abc123def"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Changelog</label>
              <textarea
                value={formData.changelog}
                onChange={(e) => setFormData({ ...formData, changelog: e.target.value })}
                placeholder="Release notes..."
                rows={3}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontFamily: 'inherit' }}
              />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.is_current}
                  onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })}
                />
                <span>Mark as current version</span>
              </label>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#28a745' }}
              onClick={createVersion}
              disabled={loading || !formData.component || !formData.version}
            >
              Create Version
            </button>
            <button 
              className="view-workflow-btn" 
              style={{ background: '#6c757d' }}
              onClick={() => {
                setShowCreateForm(false);
                setFormData({
                  component: '',
                  version: '',
                  build_number: '',
                  git_commit: '',
                  changelog: '',
                  is_current: true
                });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Current Versions Summary */}
      {currentVersions.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Current Versions (All Components)</h3>
          <div className="endpoints-table">
            <div className="table-header">
              <div>Component</div>
              <div>Version</div>
              <div>Build Number</div>
              <div>Release Date</div>
              <div>Git Commit</div>
            </div>
            {currentVersions.map(v => (
              <div key={v.id} className="table-row">
                <div><strong>{v.component}</strong></div>
                <div>{v.version}</div>
                <div>{v.build_number || '-'}</div>
                <div>{v.release_date ? new Date(v.release_date).toLocaleDateString() : '-'}</div>
                <div>{v.git_commit || '-'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <label style={{ fontWeight: '500' }}>Filter by Component:</label>
        <select
          value={selectedComponent}
          onChange={(e) => setSelectedComponent(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '200px' }}
        >
          <option value="">All Components</option>
          {components.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Versions Table */}
      <div className="workflow-table">
        <div className="table-header">
          <div>Component</div>
          <div>Version</div>
          <div>Build Number</div>
          <div>Git Commit</div>
          <div>Release Date</div>
          <div>Current</div>
          <div>Changelog</div>
          <div>Actions</div>
        </div>
        {filteredVersions.map(v => (
          <div key={v.id} className="table-row">
            <div><strong>{v.component}</strong></div>
            <div>{v.version}</div>
            <div>{v.build_number || '-'}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>{v.git_commit || '-'}</div>
            <div>{v.release_date ? new Date(v.release_date).toLocaleString() : '-'}</div>
            <div>
              {v.is_current ? (
                <span className="status-badge success">Current</span>
              ) : (
                <span className="status-badge pending">Old</span>
              )}
            </div>
            <div className="message-preview" title={v.changelog || ''}>
              {v.changelog ? (v.changelog.length > 50 ? `${v.changelog.slice(0, 50)}...` : v.changelog) : '-'}
            </div>
            <div>
              {!v.is_current && (
                <button 
                  className="view-workflow-btn" 
                  style={{ background: '#17a2b8', minWidth: '100px' }}
                  onClick={() => updateVersionCurrent(v.id, true)}
                  disabled={loading}
                >
                  Set Current
                </button>
              )}
            </div>
          </div>
        ))}
        {filteredVersions.length === 0 && !loading && (
          <div className="table-row">
            <div style={{ textAlign: 'center', padding: '20px', gridColumn: '1 / -1' }}>
              No versions found. {selectedComponent ? `Try selecting a different component.` : 'Create a new version to get started.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VersionManagement;

